import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Hash, Search, Loader2, CheckCircle2, Cpu, Car, Calendar, Cog, Settings2, Wrench } from "lucide-react";
import { toast } from "sonner";

const ADAS_LABELS = {
  front_camera: { name: "Front Camera", icon: "📷" },
  front_radar_acc: { name: "Front Radar (ACC)", icon: "📡" },
  rear_radar_bsm: { name: "Rear Radar (Blind Spot)", icon: "🛡️" },
  surround_view_360: { name: "360° Surround Cameras", icon: "🎥" },
  park_sensors_ultrasonic: { name: "Park Sensors", icon: "🅿️" },
  lane_departure_warning: { name: "Lane Departure Warning", icon: "↔️" },
  lane_keep_assist: { name: "Lane Keep Assist", icon: "🛣️" },
  auto_emergency_braking: { name: "Auto Emergency Brake", icon: "🛑" },
  adaptive_headlamps: { name: "Adaptive Headlamps", icon: "💡" },
  hud_head_up_display: { name: "Head-Up Display", icon: "🪟" },
  night_vision: { name: "Night Vision", icon: "🌙" },
  steering_angle_sensor: { name: "Steering Angle Sensor", icon: "🎯" },
  traffic_sign_recognition: { name: "Traffic Sign Recognition", icon: "🚸" },
};

export default function VinDecoder() {
  const [vin, setVin] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => { api.get("/vin/recent").then((r) => setRecent(r.data)).catch(() => {}); }, [data]);

  const decode = async (vinToDecode = vin) => {
    const v = vinToDecode.trim().toUpperCase();
    if (v.length < 11) return toast.error("VIN must be 11–17 chars");
    setLoading(true);
    setData(null);
    try {
      const r = await api.get(`/vin/decode/${v}`);
      setData(r.data);
      toast.success(`Decoded ${r.data.year} ${r.data.make} ${r.data.model}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decode failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="vin-decoder-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-500/20 flex items-center justify-center">
          <Hash className="w-6 h-6 text-blue-400" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">ADAS Intelligence</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">VIN Decoder</h1>
          <p className="text-sm text-slate-400 mt-1">Real-time vehicle decode via NHTSA vPIC · ADAS systems inference · required calibrations</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              data-testid="vin-input"
              placeholder="Enter 17-character VIN (e.g. 5YJ3E1EA4KF317329)"
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && decode()}
              maxLength={17}
              className="pl-9 bg-[#030712] border-slate-800 font-mono"
            />
          </div>
          <Button data-testid="vin-decode-btn" onClick={() => decode()} disabled={loading} className="bg-blue-600 hover:bg-blue-500 min-w-[120px]">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Decode"}
          </Button>
        </div>
        <div className="flex gap-2 mt-3">
          {["5YJ3E1EA4KF317329", "WBA8E9G56JNU52131", "1FTFW1ET5DFC18361", "JTHBA1D24G5021829"].map((sample) => (
            <button
              key={sample}
              data-testid={`vin-sample-${sample}`}
              onClick={() => { setVin(sample); decode(sample); }}
              className="text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded bg-slate-900/60 border border-slate-800 text-slate-400 hover:border-blue-500/40 hover:text-blue-300"
            >
              {sample}
            </button>
          ))}
        </div>
      </div>

      {data && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="vin-result">
          <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-[#0B1120] p-6">
            <div className="flex items-center gap-3 mb-5">
              <Car className="w-8 h-8 text-blue-400" />
              <div>
                <div className="font-display text-2xl font-semibold">{data.year} {data.make} {data.model}</div>
                <div className="text-xs text-slate-500 font-mono">{data.vin}</div>
              </div>
              {data.from_cache && <Badge className="ml-auto bg-slate-800 border-slate-700 text-slate-400 text-[10px]">FROM CACHE</Badge>}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <SpecItem icon={Calendar} label="Year" value={data.year} />
              <SpecItem icon={Cpu} label="Trim" value={data.trim} />
              <SpecItem icon={Cog} label="Engine" value={data.engine} />
              <SpecItem icon={Settings2} label="Drivetrain" value={data.drivetrain} />
              <SpecItem icon={Settings2} label="Transmission" value={data.transmission} />
              <SpecItem icon={Car} label="Body" value={data.body_class} />
              <SpecItem icon={Wrench} label="Doors" value={data.doors} />
              <SpecItem icon={Cpu} label="Plant" value={data.plant} />
              <SpecItem icon={Cpu} label="Type" value={data.vehicle_type} />
            </div>
            <div className="mt-4 pt-4 border-t border-slate-800">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Manufacturer</div>
              <div className="text-sm text-slate-300">{data.manufacturer}</div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" />ADAS Equipment (Inferred)
            </div>
            <div className="space-y-1.5">
              {Object.entries(data.adas_systems).map(([key, equipped]) => {
                const meta = ADAS_LABELS[key] || { name: key, icon: "•" };
                return (
                  <div key={key} className={`flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg ${equipped ? "bg-emerald-500/5 border border-emerald-500/15" : "bg-slate-900/40 border border-slate-800/40"}`}>
                    <span className={equipped ? "text-slate-200" : "text-slate-600 line-through"}>{meta.icon} {meta.name}</span>
                    <span className={`text-[9px] uppercase tracking-wider font-bold ${equipped ? "text-emerald-400" : "text-slate-600"}`}>{equipped ? "Yes" : "No"}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-3 rounded-xl border border-blue-500/20 bg-blue-500/5 p-6">
            <div className="text-[10px] uppercase tracking-wider font-bold text-blue-300 mb-3">Required Calibrations · Estimated</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.required_calibrations.map((c, idx) => (
                <div key={idx} className="rounded-lg bg-slate-900/40 border border-slate-800/60 p-3">
                  <div className="font-display text-sm font-medium">{c.system}</div>
                  <div className="text-xs text-slate-500 mt-1">{c.calibration}</div>
                  <div className="text-[10px] text-slate-500 mt-1.5">Trigger: <span className="text-slate-400">{c.trigger}</span></div>
                  <div className="font-mono text-blue-300 text-sm mt-2">${c.typical_fee.toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-500 mt-4 pt-3 border-t border-slate-800/60">
              Total est. fees: <span className="font-mono text-blue-300">${data.required_calibrations.reduce((s, c) => s + c.typical_fee, 0).toLocaleString()}</span>
              <span className="ml-4">· Source: {data.decoded_by}</span>
              <span className="ml-4">· {data.raw_count} attributes returned by NHTSA</span>
            </div>
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3">Recent Decodes</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
            {recent.map((r) => (
              <button
                key={r.vin}
                data-testid={`recent-${r.vin}`}
                onClick={() => { setVin(r.vin); decode(r.vin); }}
                className="text-left rounded-lg bg-slate-900/40 border border-slate-800 hover:border-blue-500/40 p-3 transition-colors"
              >
                <div className="text-xs font-medium">{r.year} {r.make} {r.model}</div>
                <div className="text-[10px] text-slate-500 font-mono">{r.vin}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const SpecItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-sm text-slate-200 truncate">{value || "—"}</div>
    </div>
  </div>
);
